import React, { useCallback, useEffect, useState } from "react";
import { useRoomContext } from "@livekit/components-react";
import { toast } from "sonner";
import { Monitor, MonitorOff } from "lucide-react";
import { api } from "@/lib/api";

/**
 * LiveScreenShare — host-only "Share your screen to the whole room"
 * button.
 *
 * Two publish paths:
 *   1. Desktop / iPadOS Safari 16.4+: browser `getDisplayMedia` via
 *      LiveKit's `setScreenShareEnabled`.
 *   2. Native Android APK (Capacitor wrap + NeoSyncScreenShare plugin):
 *      the JS bridge asks the plugin to start Android's `MediaProjection`
 *      and publish the screen as a *secondary* LiveKit participant
 *      (identity `${user_id}_screen`) so the broadcaster's main camera
 *      keeps running.
 *
 * When neither path is available the button is hidden.
 */

// Native bridge (populated by the NeoSyncScreenShare Capacitor plugin
// on Android APK builds). Undefined in the browser.
const nativeBridge = typeof window !== "undefined"
  ? window.NeoSyncScreenShare
  : undefined;

export default function LiveScreenShare() {
  const room = useRoomContext();
  const [sharing, setSharing] = useState(false);
  const [busy, setBusy] = useState(false);

  const webSupported = typeof navigator !== "undefined"
    && navigator.mediaDevices
    && typeof navigator.mediaDevices.getDisplayMedia === "function";
  const nativeSupported = !!nativeBridge;
  const supported = webSupported || nativeSupported;

  // Keep local UI state in sync with LiveKit's actual publication state.
  // For the native path we also poll the plugin because the screencast
  // participant lives in a separate LiveKit session.
  useEffect(() => {
    if (!room?.localParticipant) return;
    const lp = room.localParticipant;
    const check = async () => {
      let on = !!lp.getTrackPublication?.("screen_share")?.track
        || Array.from(lp.trackPublications?.values?.() || [])
             .some((p) => p.source === "screen_share" && p.track);
      if (!on && nativeBridge) {
        try { on = !!(await nativeBridge.isSharing())?.sharing; }
        catch { /* ignore */ }
      }
      setSharing(on);
    };
    check();
    const onPublished = () => check();
    const onUnpublished = () => check();
    lp.on("localTrackPublished", onPublished);
    lp.on("localTrackUnpublished", onUnpublished);
    return () => {
      lp.off("localTrackPublished", onPublished);
      lp.off("localTrackUnpublished", onUnpublished);
    };
  }, [room]);

  const toggle = useCallback(async () => {
    if (!room?.localParticipant) return;
    if (busy) return;
    setBusy(true);
    try {
      const next = !sharing;
      if (nativeBridge) {
        // -------- Native Android path -----------------------------
        if (next) {
          // Fetch a secondary publisher token so the screencast
          // participant is separate from the browser session.
          const { data } = await api.post(
            `/live/${room.name}/screencast-token`,
          );
          await nativeBridge.start({
            serverUrl: data.server_url,
            token: data.token,
          });
        } else {
          await nativeBridge.stop();
        }
        setSharing(next);
        toast.success(next
          ? "Screen sharing — everyone in the room can see this"
          : "Stopped sharing screen");
      } else {
        // -------- Web path (Chrome/Firefox desktop, Safari 16.4+) ---
        await room.localParticipant.setScreenShareEnabled(next, {
          audio: next,             // share tab/system audio if the picker offers it
          resolution: { width: 1920, height: 1080 },
        });
        setSharing(next);
        if (next) toast.success("Screen sharing — everyone in the room can see this");
        else toast.success("Stopped sharing screen");
      }
    } catch (err) {
      const name = err?.name || "";
      if (name === "NotAllowedError") {
        // User dismissed the picker — silent no-op.
      } else {
        toast.error(err?.message || "Couldn't start screen share");
      }
    } finally {
      setBusy(false);
    }
  }, [room, sharing, busy]);

  if (!supported) return null;

  return (
    <button
      onClick={toggle}
      disabled={busy}
      data-testid="live-screenshare-toggle"
      aria-pressed={sharing}
      aria-label={sharing ? "Stop sharing screen" : "Share screen with viewers"}
      title={sharing ? "Stop sharing screen" : "Share your screen with everyone"}
      className={`p-2 transition-colors flex items-center gap-1 ${
        sharing
          ? "bg-[#39FF14] text-black shadow-[0_0_16px_rgba(57,255,20,0.55)]"
          : "bg-white/10 hover:bg-white/20 text-white/80"
      }`}
    >
      {sharing ? <MonitorOff className="w-4 h-4" /> : <Monitor className="w-4 h-4" />}
    </button>
  );
}
